// apps/platform/src/features/ProblemWorkspace/ProblemListDrawer.tsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { X, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useInfiniteProblems } from '@/lib/hooks/useProblems';

const DIFF: Record<string, { label: string; cls: string }> = {
  easy: { label: 'Easy', cls: 'text-green-600 dark:text-green-400' },
  medium: { label: 'Med.', cls: 'text-yellow-600 dark:text-yellow-400' },
  hard: { label: 'Hard', cls: 'text-red-600 dark:text-red-400' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  currentSlug: string;
}

export function ProblemListDrawer({ open, onClose, currentSlug }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteProblems(debouncedQ);
  const problems = data?.pages.flatMap((p) => p.problems) ?? [];
  const total = data?.pages[0]?.total ?? problems.length;

  // Debounce the search box before hitting the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Keep mounted during the exit transition; toggle `shown` to slide/fade in and out.
  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 200);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  useEffect(() => {
    if (!mounted) return;
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: '200px' },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [mounted, hasNextPage, isFetchingNextPage, fetchNextPage, problems.length]);

  if (!mounted) return null;

  const go = (slug: string) => {
    navigate({ to: '/problems/$slug', params: { slug } });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Problem list">
      <div
        className={cn(
          'absolute inset-0 bg-black/40 transition-opacity duration-200',
          shown ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          'relative flex h-full w-full max-w-md flex-col border-r border-border bg-card shadow-xl',
          'transition-transform duration-200 ease-out',
          shown ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">Problem List</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{total} Problems</span>
            <button
              onClick={onClose}
              aria-label="Close problem list"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {/* Search */}
        <div className="border-b border-border p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search questions"
              aria-label="Search questions"
              className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        {/* List */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading…
            </div>
          ) : problems.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No problems found.</p>
          ) : (
            <>
              <ul>
                {problems.map((p, i) => {
                  const d = DIFF[p.difficulty] ?? { label: p.difficulty, cls: 'text-muted-foreground' };
                  const active = p.slug === currentSlug;
                  return (
                    <li key={p.slug}>
                      <button
                        onClick={() => go(p.slug)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors',
                          active ? 'bg-muted font-semibold' : 'hover:bg-muted/60',
                        )}
                      >
                        <span className="truncate pr-3">
                          {i + 1}. {p.title}
                        </span>
                        <span className={cn('shrink-0 text-xs font-medium', d.cls)}>{d.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {/* Infinite-scroll sentinel + loading indicator */}
              <div ref={sentinelRef} className="h-px" aria-hidden="true" />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading more…
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
